import Http from '@/services/http';
import { grammaticalJoin } from '@/services/arrayUtils';
import serviceExceptions from '@/domain/data/installation/engineering/isolationAging/serviceExceptions';
import {
  IsolationAgingStatus,
  IsolationAgingLifeExpectancy,
  IsolationAgingLifeExpectancyRequest,
  IsolationAgingCriterion,
  IsolationAgingWindingByCriterion,
  IsolationAgingParametersResponse,
  BaseIsolationAgingChartsRequest,
  IsolationAgingCriticalHourHistogram,
  CriteriaIdQueryRequest,
  IsolationAgingCriticalHourChart,
  IsolationAgingRemainingLifeByCriterion,
  IsolationAgingChart,
  IsolationAgingPaperHumiditySource,
} from 'sigma-mappings';
import {
  getFormattedNumber,
  getFormattedDate,
  getFieldName,
  isNumber,
  isDate,
} from '@/services/exceptionHelper';
import { formats } from '@/constants/localizer';

const urls = {
  parametersBaseUrl: assetModuleId => `engenharia/${assetModuleId}/envelhecimento-isolacao/parametros`,
  lifeExpectancyBaseUrl: assetModuleId => `engenharia/${assetModuleId}/envelhecimento-isolacao/expectativa-vida-util`,
  getStatus: assetModuleId => `engenharia/${assetModuleId}/envelhecimento-isolacao/status`,
  isolationCriterion: assetModuleId => `engenharia/${assetModuleId}/envelhecimento-isolacao/criterios`,
  windingByCriteria: (assetModuleId, windingId) => `engenharia/${assetModuleId}/envelhecimento-isolacao/enrolamentos/${windingId}`,
  criticalHour: assetModuleId => `engenharia/${assetModuleId}/envelhecimento-isolacao/hora-critica`,
  remainingLife: assetModuleId => `engenharia/${assetModuleId}/envelhecimento-isolacao/enrolamentos/vida-restante`,
  humiditySource: assetModuleId => `engenharia/${assetModuleId}/envelhecimento-isolacao/parametros/fonte-umidade-papel`,
  agingChart: assetModuleId => `engenharia/${assetModuleId}/envelhecimento-isolacao/grafico`,
  criticalHourHistogramChart: assetModuleId => `engenharia/${assetModuleId}/envelhecimento-isolacao/grafico/histograma-hora-critica`,
};

/* istanbul ignore next */
const getFormattedValue = (value, precision) => {
  if (isNumber(value)) {
    const number = parseFloat(value.replace(/,/g, '.'));
    return getFormattedNumber(number, precision);
  }

  if (isDate(value)) {
    return getFormattedDate(value, formats.DATETIME_SHORT_WITH_SECONDS);
  }

  return value;
};

/* istanbul ignore next */
const error = {
  errorHandling: {
    responseMessageParams: {
      $MA001: (data) => {
        const returnedField = serviceExceptions[data.parameters[0]];
        return {
          NomeDoCampo: getFieldName(returnedField.label),
        };
      },
      $MA025: (data) => {
        if (!data) {
          return null;
        }
        const returnedField = serviceExceptions[data.field];
        return {
          NomeDoCampo: getFieldName(returnedField.label),
        };
      },
      $MA029: (data) => {
        const returnedField = serviceExceptions[data.parameters[2]];
        return {
          FaixaInicial: getFormattedValue(data.parameters[0], returnedField.precision),
          FaixaFinal: getFormattedValue(data.parameters[1], returnedField.precision),
          NomeDoCampo: getFieldName(returnedField.label),
        };
      },
      $MA059: (data) => {
        if (!data) {
          return null;
        }
        const returnedField = serviceExceptions[data.field];
        return {
          NomeDoCampo: getFieldName(returnedField.label),
        };
      },
      $MA078: (data) => {
        const parameters = Object.entries(data.parameters);
        const commutator = parameters[0][1];
        const variablesArray = parameters
          .slice(1, parameters.length)
          .map(item => item[1]);
        const variables = grammaticalJoin(variablesArray);
        return {
          variables,
          commutator,
        };
      },
      $MA085: data => ({
        variable: data.parameters[0],
      }),
    },
  },
};

export default {
  getStatus(assetModuleId) {
    const http = new Http(error);
    return http
      .get(urls.getStatus(assetModuleId))
      .then(({ data }) => new IsolationAgingStatus(data));
  },
  getLifeExpectancy(assetModuleId) {
    const http = new Http(error);
    return http
      .get(urls.lifeExpectancyBaseUrl(assetModuleId))
      .then(({ data }) => new IsolationAgingLifeExpectancy(data));
  },
  editLifeExpectancy(assetModuleId, payload) {
    const http = new Http(error);
    const params = [];
    payload.forEach((item) => {
      params.push(new IsolationAgingLifeExpectancyRequest(item));
    });
    return http.put(urls.lifeExpectancyBaseUrl(assetModuleId), params);
  },
  getCriterion(assetModuleId) {
    const http = new Http(error);
    return http
      .get(urls.isolationCriterion(assetModuleId))
      .then(({ data }) => data.map(item => new IsolationAgingCriterion(item)));
  },
  getHumiditySource(assetModuleId) {
    const http = new Http(error);
    return http
      .get(urls.humiditySource(assetModuleId))
      .then(({ data }) => data.map(item => new IsolationAgingPaperHumiditySource(item)));
  },
  getWindingById(assetModuleId, windingId, criteriaId) {
    const http = new Http(error);
    const params = new CriteriaIdQueryRequest({ criteriaId });
    return http.get(urls.windingByCriteria(assetModuleId, windingId), { params })
      .then(({ data }) => new IsolationAgingWindingByCriterion(data));
  },
  getWindingRemainingLifeByCriterion(assetModuleId) {
    const http = new Http(error);
    return http.get(
      urls.remainingLife(assetModuleId),
    )
      .then(({ data }) => data.map(item => new IsolationAgingRemainingLifeByCriterion(item)));
  },
  getParameters(assetModuleId) {
    const http = new Http(error);
    return http
      .get(urls.parametersBaseUrl(assetModuleId))
      .then(({ data }) => new IsolationAgingParametersResponse(data));
  },
  editParameters(assetModuleId, payload) {
    const http = new Http(error);
    const mappedPayload = new IsolationAgingParametersResponse.MapBack(payload);
    return http.put(urls.parametersBaseUrl(assetModuleId), mappedPayload);
  },
  getCriticalHourDistribution(assetModuleId, criteriaId) {
    const http = new Http(error);
    const params = new CriteriaIdQueryRequest({ criteriaId });
    return http.get(urls.criticalHour(assetModuleId), { params })
      .then(({ data }) => data.map(item => new IsolationAgingCriticalHourChart(item)));
  },
  getCriticalHourHistogramChart(assetModuleId, payload) {
    const http = new Http(error);
    const params = new BaseIsolationAgingChartsRequest(payload);
    return http
      .get(urls.criticalHourHistogramChart(assetModuleId), { params })
      .then(({ data }) => data.map(item => new IsolationAgingCriticalHourHistogram(item)));
  },
  getAgingChart(assetModuleId, payload) {
    const http = new Http(error);
    const params = new BaseIsolationAgingChartsRequest(payload);
    return http
      .get(urls.agingChart(assetModuleId), { params })
      .then(({ data }) => data.map(item => new IsolationAgingChart(item)));
  },
};
